import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
  ColorByOption,
  DimensionLabelType,
  DimensionPointCategory,
  DistanceFormat,
  MemberLabelOptions,
  PlateDetailSettings,
  SnapSettings,
} from '@paragon/contract-library';

import {
  automaticRenderMode,
  getRenderModeString,
  RenderMode,
  RenderModeString,
  setRenderMode,
} from '../../library/renderModes';
import {
  LayoutPolygonVisualMode,
  layoutPolygonVisualModeIsSelectable,
} from '../../three/layout/polygon-rendering/common/mode';
import { applicationOnPickingPlane, isTopOrBottomCutPickingTargetInfo } from './applicationSlice';
import { layoutSlice } from './layoutSlice';
import { __UNSAFE_designItemsSelected } from './selectionsSlice';
import { applyUserSettings } from './userSettingsSlice';

type ShearLineViewMode = 'NONE' | 'MERGED' | 'UNMERGED';

export type FeaturePatternContainerRenderMode = RenderMode<LayoutPolygonVisualMode>;
export type FeatureAreaRenderMode = RenderMode<LayoutPolygonVisualMode>;
export type RoofRenderMode = RenderMode<LayoutPolygonVisualMode>;
export type CeilingRenderMode = RenderMode<LayoutPolygonVisualMode>;
export type EnvelopeRenderMode = RenderMode<'envelope' | 'components' | 'trussSolids'>;
export type ConnectionRenderMode = RenderMode<'hardware' | 'connections' | 'hardwareAndConnections'>;
export type WallRenderMode = RenderMode<'wireframe' | 'hatched-wireframe' | 'solid'>;
export type NonStructuralBearingRenderMode = RenderMode<'wireframe' | 'hatched-wireframe' | 'solid'>;
export type BeamRenderMode = RenderMode<'wireframe' | 'hatched-wireframe' | 'solid'>;
export type WorkLineRenderMode = RenderMode<'visible'>;

interface MutableVisualOptions {
  snapSettings: SnapSettings;
  shearLineViewMode: ShearLineViewMode;
  showAnalogPanels: boolean;

  /**
   * Note that this can be situationally overridden.  To get the ColorByOption that should be applied to the active
   *    component design, use the getActiveColorByOption selector.
   */
  colorBy: ColorByOption;

  /**
   * Sometimes we want to temporarily override the ColorByOption on the currently selected component, and have that
   *   change go away as soon as the user changes options or switches components.  This property accomplishes that.
   */
  temporaryColorByOverride: ColorByOption | null;

  synchronizeCamera: boolean;
  hiddenObjectGuids: string[];
  transparentObjectGuids: string[];
  showLabels: boolean;
  showDimensionsForSelectedItem: boolean;
  visibleDimensionCategory: DimensionPointCategory;
  dimensionLabelType: DimensionLabelType;
  showPitch: boolean;
  showSelectedMemberCutAngles: boolean;
  showSelectedMemberLengthDimensions: boolean;
  visibleLoadGuids: { [guid: string]: true };
  showAnalogModel: boolean;
  showReferenceLines: boolean;
  showCheckResultsLoadCases: boolean;
  memberLabelOptions: MemberLabelOptions;
  showFieldInstalledMembers: boolean;
  showPlates: boolean;
  showPlateNames: boolean;
  plateDetailSettings: PlateDetailSettings;
  showLoadsUnprojected: boolean;
  showManualLoadsByDefault: boolean;
  displayedForces: string[];
  showStandardDimensions: boolean;
  distanceFormat: DistanceFormat;
  highVisibilityMode: boolean;
  showGrid: boolean;

  featurePatternContainerMode: FeaturePatternContainerRenderMode;
  featureAreaMode: FeatureAreaRenderMode;
  roofMode: RoofRenderMode;
  ceilingMode: CeilingRenderMode;
  envelopeMode: EnvelopeRenderMode;
  connectionMode: ConnectionRenderMode;
  wallMode: WallRenderMode;
  nonStructuralBearingMode: NonStructuralBearingRenderMode;
  beamMode: BeamRenderMode;
  workLineMode: WorkLineRenderMode;

  /**
   * Snapping underlays only have a global visibility toggle instead of render modes. Granular per-underlay visibility
   *   is managed by the snappingUnderlaySlice.
   */
  snappingUnderlaysVisible: boolean;

  showRunDimensionsInLayout: boolean;
  showBearingDimensionsInLayout: boolean;
}

export type RenderModeType =
  | 'featurePatternContainerMode'
  | 'featureAreaMode'
  | 'roofMode'
  | 'ceilingMode'
  | 'envelopeMode'
  | 'wallMode'
  | 'nonStructuralBearingMode'
  | 'beamMode'
  | 'connectionMode'
  | 'workLineMode';

export type UnknownRenderMode = VisualOptions[RenderModeType];
export type UnknownRenderModeString = RenderModeString<UnknownRenderMode>;

export type SetManualRenderModePayload = {
  [T in RenderModeType]: { type: T; mode: RenderModeString<VisualOptions[T]> };
}[RenderModeType];

export function setRenderModeToAutomatic(type: RenderModeType) {
  return visualOptionsSlice.actions.setAutomaticRenderMode({ key: type, automatic: true });
}

export function setRenderModeToManual(type: RenderModeType) {
  return visualOptionsSlice.actions.setAutomaticRenderMode({ key: type, automatic: false });
}

export function setRenderModeControlledItemVisible(type: RenderModeType, visible: boolean) {
  return visualOptionsSlice.actions.setRenderModeControlledItemVisibility({ key: type, visible });
}

export type VisualOptions = Readonly<MutableVisualOptions>;

const initialState: VisualOptions = {
  snapSettings: {
    showIntersectionSnaps: true,
    showCentroidSnaps: true,
    showVertexSnaps: true,
    showMidLineSnaps: true,
  },
  shearLineViewMode: 'NONE',
  showAnalogPanels: false,
  colorBy: 'LumberProperties',
  temporaryColorByOverride: null,
  synchronizeCamera: false,
  hiddenObjectGuids: [],
  transparentObjectGuids: [],
  showLabels: true,
  showDimensionsForSelectedItem: true,
  visibleDimensionCategory: 'General',
  dimensionLabelType: 'Combined',
  showPitch: true,
  showSelectedMemberCutAngles: true,
  showSelectedMemberLengthDimensions: true,
  visibleLoadGuids: {},
  showAnalogModel: false,
  showReferenceLines: false,
  showCheckResultsLoadCases: false,
  memberLabelOptions: {
    nonWebLabelContent: 'MemberName',
    webLabelContent: 'MemberName',
  },
  showFieldInstalledMembers: true,
  showPlates: true,
  showPlateNames: true,
  plateDetailSettings: {
    showPlateDetails: false,
    hideForMostCommonSize: false,
    hideForMostCommonType: false,
  },
  showLoadsUnprojected: true,
  showManualLoadsByDefault: true,
  displayedForces: [],
  showStandardDimensions: true,
  distanceFormat: 'Architectural',
  highVisibilityMode: false,
  showGrid: true,

  featurePatternContainerMode: automaticRenderMode('translucent'),
  featureAreaMode: automaticRenderMode('solid'),
  roofMode: automaticRenderMode('translucent'),
  ceilingMode: automaticRenderMode('translucent'),
  envelopeMode: automaticRenderMode('components'),
  wallMode: automaticRenderMode('hatched-wireframe'),
  nonStructuralBearingMode: automaticRenderMode('hatched-wireframe'),
  beamMode: automaticRenderMode('hatched-wireframe'),
  connectionMode: automaticRenderMode('hardware'),
  workLineMode: automaticRenderMode('visible'),
  snappingUnderlaysVisible: true,
  showRunDimensionsInLayout: true,
  showBearingDimensionsInLayout: true,
};

const visualOptionsSlice = createSlice({
  name: 'visualOptions',
  initialState,
  reducers: {
    setSnapSettings: (state, action: PayloadAction<SnapSettings>) => {
      state.snapSettings = action.payload;
    },
    setShowReferenceLines: (state, action: PayloadAction<boolean>) => {
      state.showReferenceLines = action.payload;
    },
    toggleSynchronizeCamera: (state) => {
      state.synchronizeCamera = !state.synchronizeCamera;
    },
    toggleShowAnalogPanels: (state) => {
      state.showAnalogPanels = !state.showAnalogPanels;
    },
    hiddenItemGuidsPush: (state, action: PayloadAction<string>) => {
      state.hiddenObjectGuids.push(action.payload);
    },
    hiddenItemGuidsRemove: (state, action: PayloadAction<string>) => {
      const index = state.hiddenObjectGuids.indexOf(action.payload);
      state.hiddenObjectGuids.splice(index, 1);
    },
    transparentItemGuidsPush: (state, action: PayloadAction<string>) => {
      state.transparentObjectGuids.push(action.payload);
    },
    transparentGuidsRemove: (state, action: PayloadAction<string>) => {
      const index = state.transparentObjectGuids.indexOf(action.payload);
      state.transparentObjectGuids.splice(index, 1);
    },
    toggleShowLabels: (state) => {
      state.showLabels = !state.showLabels;
    },
    toggleDimensions: (state) => {
      state.showDimensionsForSelectedItem = !state.showDimensionsForSelectedItem;
    },
    setVisibleDimensionCategory: (state, action: PayloadAction<DimensionPointCategory>) => {
      state.visibleDimensionCategory = action.payload;
    },
    setDimensionLabelType: (state, action: PayloadAction<DimensionLabelType>) => {
      state.dimensionLabelType = action.payload;
    },
    toggleShowPitch: (state) => {
      state.showPitch = !state.showPitch;
    },
    toggleShowSelectedMemberCutAngles: (state) => {
      state.showSelectedMemberCutAngles = !state.showSelectedMemberCutAngles;
    },
    toggleShowSelectedMemberLengthDimensions: (state) => {
      state.showSelectedMemberLengthDimensions = !state.showSelectedMemberLengthDimensions;
    },
    showLoads: (state, action: PayloadAction<string[]>) => {
      for (const guid of action.payload) {
        if (!state.visibleLoadGuids[guid]) {
          state.visibleLoadGuids[guid] = true;
        }
      }
    },
    hideLoads: (state, action: PayloadAction<string[]>) => {
      for (const guid of action.payload) {
        if (state.visibleLoadGuids[guid]) {
          delete state.visibleLoadGuids[guid];
        }
      }
    },
    setVisibleLoads: (state, action: PayloadAction<string[]>) => {
      state.visibleLoadGuids = Object.fromEntries(action.payload.map((guid) => [guid, true]));
    },
    setColorBy: (state, action: PayloadAction<ColorByOption>) => {
      state.colorBy = action.payload;

      // When the "color by" setting is manually changed the user should see that change take effect immediately.  So
      //   if a temporary color by override is active it must be removed.
      state.temporaryColorByOverride = null;
    },
    setTemporaryColorBy: (state, action: PayloadAction<ColorByOption>) => {
      state.temporaryColorByOverride = action.payload;
    },
    setShearLineViewMode: (state, action: PayloadAction<ShearLineViewMode>) => {
      state.shearLineViewMode = action.payload;
    },
    setShowAnalogModel: (state, action: PayloadAction<boolean>) => {
      state.showAnalogModel = action.payload;
    },
    setShowCheckResultsLoadCases: (state, action: PayloadAction<boolean>) => {
      state.showCheckResultsLoadCases = action.payload;
    },
    setMemberLabelOptions: (state, action: PayloadAction<MemberLabelOptions>) => {
      state.memberLabelOptions = action.payload;
    },
    toggleShowFieldInstalledMembers: (state) => {
      state.showFieldInstalledMembers = !state.showFieldInstalledMembers;
    },
    toggleShowPlates: (state) => {
      state.showPlates = !state.showPlates;
      if (!state.showPlates) {
        state.showPlateNames = false;
      }
    },
    toggleShowPlateNames: (state) => {
      state.showPlateNames = !state.showPlateNames;
    },
    setPlateDetailSettings: (state, action: PayloadAction<PlateDetailSettings>) => {
      state.plateDetailSettings = action.payload;
    },
    toggleShowLoadsUnprojected: (state) => {
      state.showLoadsUnprojected = !state.showLoadsUnprojected;
    },
    toggleShowManualLoadsByDefault: (state) => {
      state.showManualLoadsByDefault = !state.showManualLoadsByDefault;
    },
    setComponentForcesDisplayedForces: (state, action: PayloadAction<string[]>) => {
      state.displayedForces = action.payload;
    },
    setShowStandardDimensions: (state, action: PayloadAction<boolean>) => {
      state.showStandardDimensions = action.payload;
    },

    setManualRenderModeValue: (state, action: PayloadAction<SetManualRenderModePayload>) => {
      state[action.payload.type].hidden = false;
      setRenderModeOnState(action.payload.type, setRenderMode(action.payload.mode, state[action.payload.type]));

      function setRenderModeOnState<T extends RenderModeType>(type: T, renderMode: VisualOptions[T]) {
        state[type] = renderMode;
      }
    },
    setRenderModeControlledItemVisibility: (
      state,
      action: PayloadAction<{ key: RenderModeType; visible: boolean }>,
    ) => {
      state[action.payload.key].hidden = !action.payload.visible;
    },
    setAutomaticRenderMode: (state, action: PayloadAction<{ key: RenderModeType; automatic: boolean }>) => {
      state[action.payload.key].auto = action.payload.automatic;

      if (action.payload.automatic) {
        state[action.payload.key].hidden = false;
      }
    },

    setSnappingUnderlaysVisible: (state, action: PayloadAction<boolean>) => {
      state.snappingUnderlaysVisible = action.payload;
    },

    toggleRunDimensionVisibilityInLayout: (state) => {
      state.showRunDimensionsInLayout = !state.showRunDimensionsInLayout;
    },
    toggleBearingDimensionVisibilityInLayout: (state) => {
      state.showBearingDimensionsInLayout = !state.showBearingDimensionsInLayout;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(__UNSAFE_designItemsSelected, (state, action) => {
      // The color by override only applies to the currently active component and should be active only as long as it is
      //   selected.  So if a different component design is being selected, we clear the override.
      if (action.payload.changingComponentDesign) {
        state.temporaryColorByOverride = null;
      }
    });
    builder.addCase(applyUserSettings, (state, action) => {
      const result = {
        ...state,
        ...action.payload.dimensionSettings,
        ...action.payload.viewOptionSettings,
      };

      // We want changes to user settings to be immediately reflected in the active scene.  Thus, if a color by override
      //   is active it should be cleared.
      if (state.temporaryColorByOverride !== action.payload.viewOptionSettings.colorBy) {
        result.temporaryColorByOverride = null;
      }

      return result;
    });
    builder.addCase(layoutSlice.actions.roofPlaneCreated, (state, action) => {
      // Prevent the new roof plane(s) from disappearing if they were only visible because they were being inserted.
      if (action.payload.postInsertionAction === 'finish') {
        state.roofMode = castDraft(setRenderMode('translucent', state.roofMode));
      }
    });
    builder.addCase(applicationOnPickingPlane, (state, action) => {
      // for these picking modes we must transition to automatic mode if needed to ensure that some planes are visible;
      //   see note on `TOP_OR_BOTTOM_CUT_PICKING_MODES` re: why they are a special case
      if (isTopOrBottomCutPickingTargetInfo(action.payload)) {
        const { hasRoofPlanes, hasCeilingPlanes } = action.payload;

        const roofPlanesAreSelectable = layoutPolygonVisualModeIsSelectable(getRenderModeString(state.roofMode));
        const ceilingPlanesAreSelectable = layoutPolygonVisualModeIsSelectable(getRenderModeString(state.ceilingMode));

        const roofPlanesExistAndAreSelectable = hasRoofPlanes && roofPlanesAreSelectable;
        const ceilingPlanesExistAndAreSelectable = hasCeilingPlanes && ceilingPlanesAreSelectable;

        if (!roofPlanesExistAndAreSelectable && !ceilingPlanesExistAndAreSelectable) {
          if (hasRoofPlanes) {
            state.roofMode.auto = true;
            state.roofMode.hidden = false;
          }

          if (hasCeilingPlanes) {
            state.ceilingMode.auto = true;
            state.ceilingMode.hidden = false;
          }
        }
      }
    });
  },
});

export const {
  setSnapSettings,
  setShowReferenceLines,
  toggleSynchronizeCamera,
  toggleShowAnalogPanels,
  hiddenItemGuidsPush,
  hiddenItemGuidsRemove,
  transparentItemGuidsPush,
  transparentGuidsRemove,
  toggleShowLabels,
  toggleDimensions,
  setVisibleDimensionCategory,
  setDimensionLabelType,
  toggleShowPitch,
  toggleShowSelectedMemberCutAngles,
  toggleShowSelectedMemberLengthDimensions,
  showLoads,
  hideLoads,
  setVisibleLoads,
  setColorBy,
  setTemporaryColorBy,
  setShearLineViewMode,
  setShowAnalogModel,
  setShowCheckResultsLoadCases,
  setMemberLabelOptions,
  toggleShowFieldInstalledMembers,
  toggleShowPlates,
  toggleShowPlateNames,
  setPlateDetailSettings,
  toggleShowLoadsUnprojected,
  toggleShowManualLoadsByDefault,
  setComponentForcesDisplayedForces,
  setShowStandardDimensions,
  setManualRenderModeValue,
  setSnappingUnderlaysVisible,
  toggleRunDimensionVisibilityInLayout,
  toggleBearingDimensionVisibilityInLayout,
} = visualOptionsSlice.actions;
export const visualOptions = visualOptionsSlice.reducer;
